import { useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { Status } from './arc';

/**
 * Dragging a card, on pointer events rather than the HTML5 drag-and-drop API.
 *
 * Native DnD looked like the obvious fit and is not. A `draggable` container
 * takes over the pointer, so a link inside it stops being clickable — the
 * board's arc titles could not be opened at all — and its drag start is
 * sluggish enough to feel broken. Pointer events cost about the same code
 * and behave: nothing happens until the pointer has actually moved a few
 * pixels, so a click stays a click.
 *
 * A drag ARMS anywhere on the card, its title included. Refusing to arm over a
 * link was the earlier rule and it made the most natural grab — the big bold
 * title — do nothing but open the arc on release. What separates the two is
 * distance, not what sits under the press: past the threshold the click that
 * follows is swallowed, and below it the anchor is never interfered with.
 */

const DRAG_THRESHOLD_PX = 5;

export interface DropTarget {
  status: Status;
  /** Insert before this arc, or null for the end of the column. */
  beforeName: string | null;
}

export interface BoardDrag {
  draggingName: string | null;
  dropTarget: DropTarget | null;
  /** Where the pointer is while carrying, for a surface that draws a ghost.
   *  Null when nothing is being dragged. */
  pointer: { x: number; y: number } | null;
  onCardPointerDown: (name: string) => (event: ReactPointerEvent) => void;
  onBoardPointerMove: (event: ReactPointerEvent) => void;
  onBoardPointerUp: () => void;
}

export function useBoardDrag(onMove: (name: string, target: DropTarget) => void): BoardDrag {
  const start = useRef<{ name: string; x: number; y: number; started: boolean } | null>(null);
  const [draggingName, setDraggingName] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);

  const reset = () => {
    start.current = null;
    setDraggingName(null);
    setDropTarget(null);
    setPointer(null);
  };

  return {
    draggingName,
    dropTarget,
    pointer,

    onCardPointerDown: (name) => (event) => {
      if (event.button !== 0) return;
      start.current = { name, x: event.clientX, y: event.clientY, started: false };
    },

    onBoardPointerMove: (event) => {
      const from = start.current;
      if (!from) return;

      if (!from.started) {
        const moved = Math.hypot(event.clientX - from.x, event.clientY - from.y);
        if (moved < DRAG_THRESHOLD_PX) return;
        from.started = true;
        setDraggingName(from.name);
      }

      setPointer({ x: event.clientX, y: event.clientY });

      const next = targetAt(event.clientX, event.clientY);
      setDropTarget((current) =>
        current?.status === next?.status && current?.beforeName === next?.beforeName
          ? current
          : next,
      );
    },

    onBoardPointerUp: () => {
      const from = start.current;
      if (from?.started) {
        // Whether or not it lands somewhere, a press that travelled is not a
        // click — the arc must not open underneath the release.
        swallowNextClick();
        if (dropTarget && from.name !== dropTarget.beforeName) onMove(from.name, dropTarget);
      }
      reset();
    },
  };
}

/**
 * Swallows the one click a finished drag leaves behind.
 *
 * Deliberately NOT a React effect, and this is the whole reason it is written
 * out: a drop writes and re-reads the plan, the store publishes `loading`
 * synchronously, and the surface drops to its placeholder — so the board is
 * already unmounted by the time the click arrives, and an effect's cleanup
 * would have taken the listener with it. That is how a card dropped into
 * another lane still opened the arc it had just moved.
 *
 * On `window` in capture, which is earlier than React's own root listener
 * however much of the tree has gone.
 */
function swallowNextClick(): void {
  let timer: ReturnType<typeof setTimeout>;

  const stop = () => {
    window.removeEventListener('click', swallow, true);
    clearTimeout(timer);
  };

  function swallow(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    stop();
  }

  // A release over nothing clickable produces no click at all, so this has to
  // expire rather than wait for one that is never coming and eat a later one.
  timer = setTimeout(stop, 400);
  window.addEventListener('click', swallow, true);
}

/**
 * Dragging a row to reorder ONE list, where the board drags a card between
 * status columns.
 *
 * A separate hook rather than the board's, because the two speak different
 * vocabularies and only one of them is about status: the rail's holds panel
 * has a single ordered list and a drop there means "put it here", not "move
 * it to next". They share `swallowNextClick` below, which is the part that was
 * actually hard-won.
 *
 * **A row can only be dropped in the list it was picked up from.** Lists nest
 * — a storyline held by a workspace expands in place, and its members are a
 * list inside the outer one — so dragging across the boundary would be a
 * change of MEMBERSHIP wearing a reorder's clothes. The armed list is recorded
 * on pointer-down and a drop anywhere else is declined.
 */
export interface ListDrop {
  /** Which list the pointer is over: the path of the file whose list it is. */
  list: string;
  /** Insert before this member, or null for the end. */
  beforeName: string | null;
}

export interface ListDrag {
  draggingName: string | null;
  drop: ListDrop | null;
  onRowPointerDown: (name: string) => (event: ReactPointerEvent) => void;
  onListPointerMove: (event: ReactPointerEvent) => void;
  onListPointerUp: () => void;
}

export function useListDrag(
  onReorder: (list: string, name: string, beforeName: string | null) => void,
): ListDrag {
  const start = useRef<{ name: string; list: string; x: number; y: number; started: boolean } | null>(
    null,
  );
  const [draggingName, setDraggingName] = useState<string | null>(null);
  const [drop, setDrop] = useState<ListDrop | null>(null);

  const reset = () => {
    start.current = null;
    setDraggingName(null);
    setDrop(null);
  };

  return {
    draggingName,
    drop,

    onRowPointerDown: (name) => (event) => {
      if (event.button !== 0) return;
      const target = event.target as Element;
      // Rows NEST — an opened storyline's members sit inside their holder's
      // row — so a press on an inner row bubbles to the outer row's handler
      // too, and the ancestor, running last, would arm itself instead. Only
      // the innermost row under the pointer arms.
      if (target.closest('[data-arc-name]')?.getAttribute('data-arc-name') !== name) return;
      const list = target.closest<HTMLElement>('[data-hold-list]');
      if (!list) return;
      start.current = {
        name,
        list: list.dataset['holdList']!,
        x: event.clientX,
        y: event.clientY,
        started: false,
      };
    },

    onListPointerMove: (event) => {
      const from = start.current;
      if (!from) return;

      if (!from.started) {
        if (Math.hypot(event.clientX - from.x, event.clientY - from.y) < DRAG_THRESHOLD_PX) return;
        from.started = true;
        setDraggingName(from.name);
      }

      const next = rowTargetAt(event.clientX, event.clientY);
      // Out of its own list is not a drop: see the note above.
      const landed = next && next.list === from.list ? next : null;
      setDrop((current) =>
        current?.list === landed?.list && current?.beforeName === landed?.beforeName
          ? current
          : landed,
      );
    },

    onListPointerUp: () => {
      const from = start.current;
      if (from?.started) {
        swallowNextClick();
        if (drop && from.name !== drop.beforeName) onReorder(drop.list, from.name, drop.beforeName);
      }
      reset();
    },
  };
}

/** [targetAt]'s counterpart for a single list. */
function rowTargetAt(x: number, y: number): ListDrop | null {
  const element = document.elementFromPoint(x, y);
  if (!element) return null;

  const list = element.closest<HTMLElement>('[data-hold-list]');
  if (!list) return null;

  const row = element.closest<HTMLElement>('[data-arc-name]');
  if (!row || !list.contains(row)) return { list: list.dataset['holdList']!, beforeName: null };

  const box = row.getBoundingClientRect();
  const after = y > box.top + box.height / 2;
  const sibling = after ? row.nextElementSibling : row;
  return {
    list: list.dataset['holdList']!,
    beforeName: sibling?.getAttribute('data-arc-name') ?? null,
  };
}

/**
 * What is under the pointer, read from the DOM rather than tracked in state.
 * The board is a grid of ordinary elements, so asking the browser is both
 * simpler and correct when a column scrolls under the cursor.
 *
 * The dragged card is `pointer-events: none` while it travels, so this reads
 * what is BEHIND it rather than the card itself.
 */
function targetAt(x: number, y: number): DropTarget | null {
  const element = document.elementFromPoint(x, y);
  if (!element) return null;

  const column = element.closest<HTMLElement>('[data-status]');
  if (!column) return null;
  const status = column.dataset['status'] as Status;

  const card = element.closest<HTMLElement>('[data-arc-name]');
  if (!card) return { status, beforeName: null };

  // Past the midpoint means "after this card", which is "before the next
  // one" — the vocabulary the move planner already speaks.
  const box = card.getBoundingClientRect();
  const after = y > box.top + box.height / 2;
  const sibling = after ? card.nextElementSibling : card;
  const beforeName = sibling?.getAttribute('data-arc-name') ?? null;

  return { status, beforeName };
}
